import { useTranslation } from "react-i18next";
import { Actor, Gender } from "../../models/Actor";
import { Outfit } from "../../models/Outfit";
import { OutfitPickerItem, PreviewGender } from "./outfitPickerModel";
import OutfitStage from "./OutfitStage";

function Row(props: { label: string; children: React.ReactNode; testId?: string }) {
  return (
    <div
      className="flex min-h-[36px] items-center justify-between gap-[12px] border-t border-creator-border text-[14px]"
      data-testid={props.testId}
    >
      <span className="text-creator-text-sub">{props.label}</span>
      <span className="text-right font-medium text-creator-text">{props.children}</span>
    </div>
  );
}

/** The right-hand pane: a large portrait of the highlighted tile and a few facts. */
export default function OutfitPickerDetails(props: {
  item?: OutfitPickerItem;
  gender: PreviewGender;
  actor?: Actor;
  ownOutfit?: Outfit;
}) {
  const { item, gender, actor, ownOutfit } = props;
  const { t } = useTranslation();
  if (!item) return <aside className="w-[360px] flex-none border-l border-creator-border" />;

  const genderLabel = (g: Gender) =>
    g === Gender.female ? t("outfit_picker.female") : t("outfit_picker.male");

  let name: string;
  let rows: React.ReactNode;
  if (item.kind === "outfit") {
    const { outfit } = item;
    name = outfit.name;

    const actorName = actor?.workingName ?? "";
    const previewed =
      actor?.gender === gender
        ? t("outfit_picker.previewed_like_actor", { gender: genderLabel(gender), name: actorName })
        : actor?.gender === Gender.male || actor?.gender === Gender.female
          ? t("outfit_picker.previewed_other_gender", {
              gender: genderLabel(gender),
              name: actorName,
              actorGender: genderLabel(actor.gender).toLowerCase(),
            })
          : actor
            ? t("outfit_picker.previewed_random_actor", { gender: genderLabel(gender), name: actorName })
            : genderLabel(gender);

    const missing = (g: Gender) => (
      <span className="font-normal text-creator-notice-text">
        {t("outfit_picker.renders_missing", { gender: genderLabel(g).toLowerCase() })}
      </span>
    );
    const renders =
      !outfit.malePreviewUri && !outfit.femalePreviewUri ? (
        <span className="font-normal text-creator-notice-text">{t("outfit_picker.renders_none")}</span>
      ) : (
        <>
          {outfit.malePreviewUri ? genderLabel(Gender.male) : missing(Gender.male)}
          {" · "}
          {outfit.femalePreviewUri ? genderLabel(Gender.female) : missing(Gender.female)}
        </>
      );

    rows = (
      <>
        <Row label={t("outfit_picker.previewed_as")} testId="outfit-details-previewed-as">
          {previewed}
        </Row>
        <Row label={t("outfit_picker.renders")} testId="outfit-details-renders">
          {renders}
        </Row>
      </>
    );
  } else if (item.kind === "default") {
    name = t("outfit_picker.default");
    rows = (
      <Row label={t("outfit_picker.field_label")}>
        {ownOutfit
          ? t("outfit_picker.default_meta", { name: ownOutfit.name })
          : t("outfit_picker.default_meta_none")}
      </Row>
    );
  } else {
    name = t("outfit_picker.random");
    rows = <Row label={t("outfit_picker.field_label")}>{t("outfit_picker.random_meta")}</Row>;
  }

  return (
    <aside
      className="flex w-[360px] flex-none flex-col gap-[16px] overflow-y-auto border-l border-creator-border p-[24px]"
      data-testid="outfit-picker-details"
    >
      <OutfitStage item={item} gender={gender} ownOutfit={ownOutfit} />
      <div className="text-[22px] font-semibold leading-[1.2] text-creator-text break-words">{name}</div>
      <div className="flex flex-col">{rows}</div>
    </aside>
  );
}
